import React, { lazy, Suspense } from "react";
import { getInfo } from "../utils/urlUtil";
import { useCountry } from "../utils/countryUtils";
import { Route, Routes, Navigate } from "react-router-dom";
import routesConfig from "./routesConfig";

// Lazy loading de componentes
const HomePage = lazy(() => import("../pages/HomePage"));
const CompanyPage = lazy(() => import("../pages/CompanyPage"));
const ServicesUnitsPage = lazy(() => import("../pages/ServicesUnitsPage"));
const NewsPage = lazy(() => import("../pages/NewsPage"));
const ContactPage = lazy(() => import("../pages/ContactPage"));
const NewsSinglePage = lazy(() => import("../pages/NewsSinglePage"));
const SolutionsPage = lazy(() => import("../pages/SolutionsPage"));
const SmartGrowingPage = lazy(() => import("../pages/SmartGrowingPage"));
const BusinessPartnersPage = lazy(() => import("../pages/BusinessPartnersPage"));
const BioSolutionsPage = lazy(() => import("../pages/BioSolutionsPage"));
const NonCropPage = lazy(() => import("../pages/NonCropPage"));
const DesignPage = lazy(() => import("../pages/DesignPage"));
const PrivacyStatementPage = lazy(() => import("../pages/PrivacyStatementPage"));
const TermsAndConditionsPage = lazy(() => import("../pages/TermsAndConditionsPage"));
const ProductListPage = lazy(() => import("../pages/ProductListPage"));
const NotFoundPage = lazy(() => import("../pages/NotFoundPage"));
const ProductSinglePage = lazy(() => import("../pages/ProductSinglePage"));
const ProductGlobalListPage = lazy(() => import("../pages/ProductGlobalListPage"));
const ProductB2BListPage = lazy(() => import("../pages/ProductB2BListPage"));
const ProductB2BContactPage = lazy(() => import("../pages/ProductB2BContactPage"));
const InfoCn = lazy(() => import("../pages/infoCn"));
const InvestorsPage = lazy(() => import("../pages/InvestorsPage"));
const ReportsPage = lazy(() => import("../pages/ReportsPage"));
const CustomCountrySolutionPage = lazy(() => import("../pages/CustomCountrySolutionPage"));
const LocalReportPage = lazy(() => import("../pages/LocalReportPage"));

import LoadingFallback from "../components/common/LoadingFallback";
export { LoadingFallback };

const SolutionsRoute = () => {
  const urlInfo = getInfo();
  const isoCountry = urlInfo.isoCountry || "gl";

  if (isoCountry === "gl") {
    return <SolutionsPage />;
  }

  const { getCountryDataByISO, countriesData, isLoading } = useCountry();

  // Si los datos del CMS aún no cargaron, mostramos fallback para evitar
  // renderizar prematuramente ProductListPage a un país B2B o custom solutions.
  if (!countriesData || countriesData.length === 0 || isLoading) {
    return <LoadingFallback />;
  }

  const countryData = getCountryDataByISO(isoCountry);
  const countryType = countryData?.country_type;
  const countryId = countryData?.id;

  if (countryType === "internal_website_b2b" && countryId) {
    return <Navigate to={`/${urlInfo.base}/product-b2b-list/${countryId}`} replace />;
  }

  if (countryType === "internal_website_custom_solutions" || isoCountry === "us") {
    return <Navigate to={`/${urlInfo.base}/custom-solutions`} replace />;
  }

  return <ProductListPage />;
};

const InvestorsRoute = () => {
  // Solo accesible para "gl", de lo contrario redirige
  const country = getInfo().isoCountry;
  
  if (country !== "gl") {
    window.location.href = "/gl/investors";
    return <LoadingFallback />;
  }

  return <InvestorsPage />;
};

const components = {
  "/:country": HomePage,
  "/:country/company": CompanyPage,
  "/:country/serviceunits": ServicesUnitsPage,
  "/:country/news": NewsPage,
  "/:country/contact-us": ContactPage,
  "/:country/news/:id": NewsSinglePage,
  "/:country/solutions": SolutionsRoute,
  "/:country/smart-growing": SmartGrowingPage,
  "/:country/business-partners": BusinessPartnersPage,
  "/:country/bio-solutions": BioSolutionsPage,
  "/:country/enviro-solutions": NonCropPage,
  "/:country/design": DesignPage,
  "/:country/privacy-statement": PrivacyStatementPage,
  "/:country/terms-and-conditions": TermsAndConditionsPage,
  "/:country/product-list": ProductListPage,
  "/:country/product/:slug": ProductSinglePage,
  "/:country/product-global-list": ProductGlobalListPage,
  "/:country/product-b2b-list/:id": ProductB2BListPage,
  "/:country/product-b2b-contact/:id": ProductB2BContactPage,
  "/:country/info": InfoCn,
  "/:country/investors": InvestorsRoute,
  // "/:country/investors-new": InvestorsPage,
  "/:country/reports/:id/:year/:reportSlug": ReportsPage,
  "/:country/reports/:year/:reportSlug": ReportsPage,
  "/:country/reports/:id": ReportsPage,
  "/:country/custom-solutions": CustomCountrySolutionPage,
  "/:country/local-report": LocalReportPage,
  "*": NotFoundPage,
};

const RoutesList = () => (
  <Suspense fallback={<LoadingFallback />}>
    <Routes>
      {/* Redirige desde "/" a "/GL" */}
      <Route path="/thailand/th" element={<Navigate to="/th" replace />} />
      <Route path="/malaysia/en" element={<Navigate to="/my" replace />} />
      <Route path="/" element={<Navigate to="/gl" replace />} />

      {routesConfig.map(({ path }, index) => (
        <Route key={index} path={path} element={React.createElement(components[path])} />
      ))}
    </Routes>
  </Suspense>
);

export default RoutesList;
